export { connection } from './connections/mbti-disco-air-connection';
export type { Dispatchers } from './connections/mbti-disco-air-connection';
export { createSpecificClient } from './http/clients/ClientFactory';
export type { DlsAlternative, FilterCriteriaAlternative } from './http/clients/dls/DlsClientAlternative';
export type { DlsSearch, FilterCriteriaDlsSearch } from './http/clients/dls/DlsClientSearch';
export type {
  DlsTeaser,
  FilterCriteriaCampaign,
  FilterCriteriaHome,
  FilterCriteriaPi,
} from './http/clients/dls/DlsClientTeaser';
export type { OneSearchVehicleTileDataParams } from './http/clients/onesearch/graphql/OneSearchGraphQL';
export type { TCampaignTileAemPayload } from './schema/campaign-tile/CampaignTileData';
export type { VehicleDls } from './schema/dls/DeepLinkingSearchResults';
export { dcpTranslations, type DcpTranslations } from './schema/phrase/DcpTranslations';
export { otTranslationsSchema, type OTTranslations } from './schema/phrase/OTTranslations';
export type { TPersonalizedVehicle } from './schema/t4-personalization/PersonalizationData';
export type { MarketConfiguration, SharedRequestConfiguration } from './types/GeneralParameter';
export { isDlsSearchAlternative } from './types/GeneralTypes';
export type { ContextType, Vehicle } from './types/one-search/queries';
export { RawDataSchema, type RawData } from './types/raw/RequestParams';
export { appendUserContextToUrl as appendCtxToUrl } from './util/EnhanceUrlWithCtx';
export { OneSearchBrandMap, oneSearchBrandToModelMap } from './util/one-search/OneSearchBrandMap';
export { filterVehicleClasses } from './util/one-search/VehicleClassTypeGuard';
export { getEmhSearchResultsUrl } from './util/UrlUtil';
export { isCommercialVanBy } from './util/VansConfiguration';

import { mbtiDiscoAirConnectionName } from '@sccs/common';
import { InflateRequestMode } from '@sccs/types';
import { SeamlessConnection, initializeStore } from '@seamless/store';
import { v4 as uuidv4 } from 'uuid';
import { Dispatchers, connection } from './connections/mbti-disco-air-connection';
import { DlsSearch } from './http/clients/dls/DlsClientSearch';
import { DlsTeaser } from './http/clients/dls/DlsClientTeaser';
import { OneSearchVehicleTileDataParams } from './http/clients/onesearch/graphql/OneSearchGraphQL';
import { SharedRequestConfiguration, VehicleParameterUnion } from './types/GeneralParameter';
import { RawData } from './types/raw/RequestParams';

const store = initializeStore();

export function getData(config: SharedRequestConfiguration, params: VehicleParameterUnion, rawData?: RawData) {
  store.addConnection(connection as unknown as SeamlessConnection<Dispatchers, unknown>);
  const inflateIds = generateInflateIds(params, config);

  store.getConnectionDispatchers(mbtiDiscoAirConnectionName).then(({ getData }) => {
    getData(inflateIds, config, params, rawData ?? {});
  });

  return inflateIds;
}

function generateInflateIds(requestParams: VehicleParameterUnion, configuration: SharedRequestConfiguration): string[] {
  const inflateIds: string[] = [];
  const isFocMarket = configuration.isFoc;

  let amountOfIds: number;
  switch (configuration.mode) {
    case InflateRequestMode.BANNER_CAMPAIGN:
    case InflateRequestMode.SIMPLE_PRICE: {
      if (isFocMarket) {
        const oneSearchParams = requestParams as OneSearchVehicleTileDataParams;
        amountOfIds = oneSearchParams.limit ?? 1;
        break;
      } else {
        const searchAlternativeParam = requestParams as DlsSearch;
        amountOfIds = searchAlternativeParam.includeFirstVehicles;
        break;
      }
    }
    case InflateRequestMode.PI_PAGES_CARS:
    case InflateRequestMode.PI_PAGES_VANS:
    case InflateRequestMode.HOMEPAGE_CARS:
    case InflateRequestMode.HOMEPAGE_VANS:
    case InflateRequestMode.CAMPAIGN_TEASER_CARS:
    case InflateRequestMode.CAMPAIGN_TEASER_VANS: {
      if (isFocMarket) {
        const oneSearchParams = requestParams as OneSearchVehicleTileDataParams;
        amountOfIds = oneSearchParams.limit ?? 12;
        break;
      } else {
        const teaserParam = requestParams as DlsTeaser;
        amountOfIds = teaserParam.numberOfPrimaryVehicles + teaserParam.numberOfSecondaryVehicles;
        break;
      }
    }
    case InflateRequestMode.ZERO_RESULT: {
      const teaserParam = requestParams as DlsSearch;
      amountOfIds = teaserParam.numberOfPrimaryVehicles + teaserParam.numberOfSecondaryVehicles;
      break;
    }
    case InflateRequestMode.VEHICLE_MATCH_TEASER:
    case InflateRequestMode.PERSONALIZED_T4: {
      amountOfIds = 1;
      break;
    }
    default:
      throw new Error(
        `Unsupported mode provided in configuration: '${configuration.mode}'. Please ensure that the mode is one of the supported values: ${Object.values(InflateRequestMode).join(', ')}.`,
      );
  }

  for (let index = 0; index < amountOfIds; index++) {
    inflateIds.push(uuidv4());
  }

  return inflateIds;
}
